import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { DOMAIN_EMPLOYEES_QUERY, CREATE_EMPLOYEE_MUTATION } from '../graphql/employees';

interface Employee {
  id: number;
  email: string;
  username: string;
  role: string;
}

const s = {
  page: { padding: '32px 40px', maxWidth: 900, margin: '0 auto' } as React.CSSProperties,
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 } as React.CSSProperties,
  title: { fontSize: 24, fontWeight: 700, margin: 0 } as React.CSSProperties,
  addBtn: { background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 14 },
  th: { textAlign: 'left' as const, padding: '10px 14px', borderBottom: '2px solid #e5e7eb', fontWeight: 600, color: '#374151' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f3f4f6', color: '#111827' },
  roleBadge: (role: string): React.CSSProperties => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
    background: role === 'admin' ? '#dbeafe' : role === 'manager' ? '#fef3c7' : '#f3f4f6',
    color: role === 'admin' ? '#1d4ed8' : role === 'manager' ? '#92400e' : '#4b5563',
  }),
  select: { width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box' as const, background: '#fff' },
  modal: { position: 'fixed' as const, inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  dialog: { background: '#fff', borderRadius: 10, padding: 28, width: 420, boxShadow: '0 10px 40px rgba(0,0,0,0.15)' } as React.CSSProperties,
  dialogTitle: { fontSize: 18, fontWeight: 700, marginBottom: 20, marginTop: 0 } as React.CSSProperties,
  field: { marginBottom: 16 } as React.CSSProperties,
  label: { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#374151' } as React.CSSProperties,
  input: { width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box' as const },
  error: { color: '#dc2626', fontSize: 13, marginBottom: 14 } as React.CSSProperties,
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 } as React.CSSProperties,
  cancelBtn: { background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  saveBtn: { background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 14 } as React.CSSProperties,
  empty: { textAlign: 'center' as const, color: '#9ca3af', padding: '48px 0', fontSize: 15 },
};

const ROLE_LABELS: Record<string, string> = { admin: 'Admin', manager: 'Office', worker: 'Shop' };

const blank = { email: '', password: '', username: '', role: 'worker' };

export default function Employees() {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [formError, setFormError] = useState('');

  const { data, loading, refetch } = useQuery(DOMAIN_EMPLOYEES_QUERY);
  const [createEmployee, { loading: saving }] = useMutation(CREATE_EMPLOYEE_MUTATION);

  const employees: Employee[] = data?.domainEmployees || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (!form.email.trim() || !form.password || !form.username.trim()) {
      setFormError('All fields are required.');
      return;
    }
    try {
      await createEmployee({ variables: { email: form.email.trim(), password: form.password, username: form.username.trim(), role: form.role } });
      setForm(blank);
      setShowForm(false);
      refetch();
    } catch (err: any) {
      setFormError(err.message?.replace('GraphQL error: ', '').replace('Authentication failed: ', '') || 'Failed to create employee.');
    }
  };

  const handleClose = () => { setShowForm(false); setForm(blank); setFormError(''); };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>Employees</h1>
        <button style={s.addBtn} onClick={() => setShowForm(true)}>+ Add Employee</button>
      </div>

      {loading ? (
        <div style={s.empty}>Loading...</div>
      ) : employees.length === 0 ? (
        <div style={s.empty}>No employees yet. Add one to get started.</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Name</th>
              <th style={s.th}>Email</th>
              <th style={s.th}>Role</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.id}>
                <td style={s.td}>{emp.username}</td>
                <td style={s.td}>{emp.email}</td>
                <td style={s.td}><span style={s.roleBadge(emp.role)}>{ROLE_LABELS[emp.role] || emp.role}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div style={s.modal} onClick={handleClose}>
          <div style={s.dialog} onClick={(e) => e.stopPropagation()}>
            <h2 style={s.dialogTitle}>Add Employee</h2>
            {formError && <div style={s.error}>{formError}</div>}
            <form onSubmit={handleSubmit}>
              <div style={s.field}>
                <label style={s.label}>Name</label>
                <input style={s.input} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="Jane Smith" autoFocus />
              </div>
              <div style={s.field}>
                <label style={s.label}>Email</label>
                <input style={s.input} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@example.com" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Password</label>
                <input style={s.input} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 8 characters" />
              </div>
              <div style={s.field}>
                <label style={s.label}>Role</label>
                <select style={s.select} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="admin">Admin</option>
                  <option value="manager">Office</option>
                  <option value="worker">Shop</option>
                </select>
              </div>
              <div style={s.actions}>
                <button type="button" style={s.cancelBtn} onClick={handleClose}>Cancel</button>
                <button type="submit" style={s.saveBtn} disabled={saving}>{saving ? 'Creating...' : 'Create Account'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
